
import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute } from "@angular/router";

import { GlobalErrorHandlerService } from '@k12/errors';
import { TasksService } from '@k12/tasks';
import { TaskTypeService } from '@k12/tasks';
import { ToastMessageService } from '@k12/notifications';
import { ToastMessageType } from '@k12/models';
import { Task, TaskType, TaskTypeConfirmation, SubTask } from '@k12/models';
import { LoadingSpinnerService } from '@k12/loading';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-confirmation',
  imports: [CommonModule, MatButtonModule, MatIconModule],
  standalone: true,
  styleUrls: ['./confirmation.component.scss'],
  templateUrl: './confirmation.component.html'
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  private globalErrorHandlerService: GlobalErrorHandlerService = inject(GlobalErrorHandlerService);

  private route: ActivatedRoute = inject(ActivatedRoute);
  private taskService: TasksService = inject(TasksService);
  private taskTypeService: TaskTypeService = inject(TaskTypeService);
  private toastService = inject(ToastMessageService);
  private loadingSpinnerService = inject(LoadingSpinnerService);
  private unsubscribe = new Subject<void>();

  public errorMessage: string = '';
  public task: Task | null;
  public taskType: TaskType;
  public taskAction: SubTask | undefined;
  public taskTypeAction: TaskTypeConfirmation | undefined;
  public taskAccepted: boolean | null = null;
  public recentTaskNote: string = '';
  constructor() {}

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngOnInit(): void {
    const taskId = this.route.snapshot.paramMap.get('id');
    const taskActionId = this.route.snapshot.paramMap.get('typeId');

    if (!taskId || !taskActionId) {
      this.toastService.showToast(ToastMessageType.Error, 'Invalid task or task action ID.');
      this.globalErrorHandlerService.handleError({ message: 'Invalid task or task action ID.' });
      return;
    }

    this.loadingSpinnerService.showSpinner();
    this.taskService
      .get(taskId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (task) => {
          this.task = task.body;
          if (!this.task?.subTasks) {
            this.toastService.showToast(ToastMessageType.Error, 'Task has no subtasks.');
            this.globalErrorHandlerService.handleError({ message: 'Task has no subtasks.' });
            this.loadingSpinnerService.hideSpinner();
            return;
          }
          this.taskAction = this.task?.subTasks?.find(
            (subTask) => subTask.id?.toLowerCase() === taskActionId.toLowerCase()
          );
          if (!this.taskAction) {
            this.toastService.showToast(ToastMessageType.Error, 'Task action not found.');
            this.globalErrorHandlerService.handleError({ message: 'Task action not found.' });
            this.loadingSpinnerService.hideSpinner();
            return;
          }

          this.taskTypeService
            .get(this.taskAction.taskTypeId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
              next: (taskType) => {
                if (!taskType.body) {
                  return;
                }
                this.taskType = taskType.body;
                this.taskTypeAction = this.taskType.subTasks?.find(
                  (subTask) => subTask.actionId?.toLowerCase() === this.taskAction?.taskTypeSubTaskId.toLowerCase()
                ) as TaskTypeConfirmation | undefined;
                if (this.task && this.taskTypeAction?.$type === 'Confirmation') {
                  this.taskService.getTaskNotes(taskId).subscribe({
                    next: (resp) => {
                      const apiNotes = resp?.body ?? [];
                      this.recentTaskNote = apiNotes.find((note) => note.taskNoteType === 1)?.comments || '';
                      this.loadingSpinnerService.hideSpinner();
                    },
                    error: (err) => {
                      this.loadingSpinnerService.hideSpinner();
                      console.error('Failed to load task notes', err);
                    }
                  });
                } else {
                  this.loadingSpinnerService.hideSpinner();
                  this.toastService.showToast(ToastMessageType.Error, 'This is not a confirmation task template');
                  this.globalErrorHandlerService.handleError({ message: 'This is not a confirmation task template' });
                }
              },
              error: (error) => {
                this.loadingSpinnerService.hideSpinner();
                this.toastService.showToast(ToastMessageType.Error, 'Task Confirmation failed.');
                this.globalErrorHandlerService.handleError(error);
              }
            });
        },
        error: (error) => {
          this.loadingSpinnerService.hideSpinner();
          this.toastService.showToast(ToastMessageType.Error, 'Loading Task details failed.');
          this.globalErrorHandlerService.handleError(error);
        }
      });
  }

  public get checkMarkTitle(): string {
    let checkMarkTitle = 'Yes';
    if (this.taskType?.subTasks.length > 0) {
      const confirmationAction = this.taskType.subTasks[0] as TaskTypeConfirmation;
      checkMarkTitle = confirmationAction.checkMarkTitle || 'Yes';
    }
    return checkMarkTitle;
  }

  public get xMarkTitle(): string {
    let xMarkTitle = 'No';
    if (this.taskType?.subTasks.length > 0) {
      const confirmationAction = this.taskType.subTasks[0] as TaskTypeConfirmation;
      xMarkTitle = confirmationAction.xMarkTitle || 'No';
    }
    return xMarkTitle;
  }

  public toggleAccepted(accepted: boolean): void {
    if (this.taskAccepted === accepted) {
      this.taskAccepted = null;
      return;
    }
    this.taskAccepted = accepted;
  }

  public get confirmationAnswered(): boolean {
    return this.taskAccepted !== null;
  }

  public onBack(): void {
    window.history.back();
  }

  public onSubmit(): void {
    if (this.taskAccepted === null || !this.task) {
      return;
    }

    this.task.taskStatusId = this.taskAccepted ? 6 : 3; // 6 = Accepted, 3 = Denied

    this.task.subTasks?.forEach((subTask) => {
      subTask.taskStatusId = this.taskAccepted ? 6 : 3;
    });

    this.loadingSpinnerService.showSpinner();
    this.taskService
      .updateTask(this.task)
      .then(() => {
        this.loadingSpinnerService.hideSpinner();
        this.toastService.showToast(ToastMessageType.Success, 'Task updated successfully.');
        this.onBack();
      })
      .catch((error) => {
        this.loadingSpinnerService.hideSpinner();
        this.toastService.showToast(ToastMessageType.Error, 'Error updating task status.');
        this.globalErrorHandlerService.handleError(error);
      });
  }
}
